import React, { useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Recovery control for a member stuck on "check your email": correct or replace the pending
 * address while the account is still unverified. Never marks the new address verified.
 */
export const ChangePendingEmail = ({ currentEmail, lang = 'en', onChanged }) => {
  const es = lang === 'es';
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [state, setState] = useState('');
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setState('sending');
    try {
      const { data } = await axios.post(`${API}/auth/change-pending-email`, {
        email: currentEmail,
        password,
        new_email: newEmail.trim(),
      });
      setState('done');
      setPassword('');
      if (onChanged) onChanged(data.email);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      setState('');
      if (status === 429) {
        setError(es ? 'Demasiados intentos. Espera unos minutos.' : 'Too many attempts. Please wait a few minutes.');
      } else if (status === 401) {
        setError(es ? 'Contraseña incorrecta.' : "That password doesn't match this account.");
      } else if (status === 409) {
        setError(es ? 'Ese correo ya está en uso.' : 'That address is already in use.');
      } else if (typeof detail === 'string' && detail) {
        setError(detail);
      } else if (!err.response) {
        setError(es ? 'Sin conexión. Inténtalo de nuevo.' : 'No connection. Please try again.');
      } else {
        setError(es ? 'No se pudo cambiar el correo.' : "We couldn't change that address.");
      }
    }
  };

  if (state === 'done') {
    return (
      <p className="text-sm text-emerald-300/80 mt-4" data-testid="change-email-done">
        {es
          ? 'Correo actualizado. Enviamos un enlace nuevo a esa dirección.'
          : 'Address updated. We sent a new link to that address.'}
      </p>
    );
  }

  if (!open) {
    return (
      <div className="mt-4">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-sm text-white/45 hover:text-white/70 underline"
          data-testid="change-email-open"
        >
          {es ? '¿Correo equivocado? Cámbialo' : 'Wrong email address? Change it'}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-5 space-y-3 text-left" data-testid="change-email-form">
      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
        {es
          ? `Reemplazaremos ${currentEmail} y enviaremos un enlace nuevo.`
          : `We'll replace ${currentEmail} and send a new link.`}
      </p>
      <input
        type="email"
        required
        value={newEmail}
        onChange={(e) => setNewEmail(e.target.value)}
        placeholder={es ? 'Nuevo correo' : 'New email address'}
        className="w-full px-4 py-3 bg-[#12101A] border border-white/10 rounded-lg text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#D4A843]/50"
        data-testid="change-email-new-input"
      />
      <input
        type="password"
        required
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={es ? 'Tu contraseña' : 'Your password'}
        className="w-full px-4 py-3 bg-[#12101A] border border-white/10 rounded-lg text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#D4A843]/50"
        data-testid="change-email-password-input"
      />
      {error && (
        <p className="text-sm text-red-300/80" data-testid="change-email-error">{error}</p>
      )}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={state === 'sending'}
          className="flex-1 py-3 bg-[#D4A843] hover:bg-[#C49A3D] disabled:opacity-50 text-[#0A0910] text-sm font-semibold rounded-lg transition-colors"
          data-testid="change-email-submit"
        >
          {state === 'sending'
            ? (es ? 'Guardando…' : 'Saving…')
            : (es ? 'Usar este correo' : 'Use this address')}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setError(''); }}
          className="px-4 text-sm text-white/40 hover:text-white/60"
          data-testid="change-email-cancel"
        >
          {es ? 'Cancelar' : 'Cancel'}
        </button>
      </div>
    </form>
  );
};

export default ChangePendingEmail;
